import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  ArrowLeftIcon, 
  PaperAirplaneIcon,
  UserIcon,
  PhoneIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import {
  selectActiveConversation,
  selectActiveMessages,
  selectIsLoading,
  selectIsSending,
  selectError,
  closeActiveConversation,
  clearError
} from '../../store/slices/chatSlice';
import { 
  fetchMessages, 
  sendMessage, 
  markConversationMessagesAsRead 
} from '../../store/thunks/chatThunk';
import socketService from '../../services/socketService';

const ChatThread = () => {
  const dispatch = useDispatch();
  const activeConversation = useSelector(selectActiveConversation);
  const messages = useSelector(selectActiveMessages);
  const isLoading = useSelector(selectIsLoading);
  const isSending = useSelector(selectIsSending);
  const error = useSelector(selectError);
  
  const [messageText, setMessageText] = useState('');
  const [sendError, setSendError] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (activeConversation) {
      // Load messages for the active conversation
      dispatch(fetchMessages({ 
        conversationId: activeConversation._id, 
        page: 1, 
        limit: 50 
      }));

      // Join the conversation room for real-time updates
      socketService.joinConversation(activeConversation._id);

      return () => {
        // Leave the conversation room when switching away
        socketService.leaveConversation(activeConversation._id);
      };
    }
  }, [activeConversation, dispatch]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Mark messages as read when viewing the conversation
    if (activeConversation && messages.length > 0) {
      const unreadMessages = messages.filter(
        msg => msg.direction === 'incoming' && !msg.isRead
      );
      
      if (unreadMessages.length > 0) {
        const messageIds = unreadMessages.map(msg => msg._id);
        dispatch(markConversationMessagesAsRead({
          messageIds,
          conversationId: activeConversation._id
        }));
        
        // Also notify via socket
        socketService.markMessagesAsRead(activeConversation._id, messageIds);
      }
    }
  }, [activeConversation, messages, dispatch]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageText.trim() || isSending || !activeConversation) {
      return;
    }

    const message = messageText.trim();
    setMessageText('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    // Clear any previous send errors
    setSendError(null);
    
    try {
      await dispatch(sendMessage({
        conversationId: activeConversation._id,
        message
      })).unwrap();
    } catch (error) {
      console.error('Failed to send message:', error);
      
      // Restore message text on error
      setMessageText(message);
      
      // Show user-friendly error message
      let errorMessage = 'Failed to send message. Please try again.';
      
      if (error?.message) {
        // Parse common error types for better user experience
        if (error.message.includes('validation failed')) {
          errorMessage = 'Message could not be sent due to a formatting issue. Please try again.';
        } else if (error.message.includes('phone number')) {
          errorMessage = 'There was an issue with the phone number. Please contact support.';
        } else if (error.message.includes('network') || error.message.includes('timeout')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('authentication') || error.message.includes('permission')) {
          errorMessage = 'You do not have permission to send messages. Please refresh and try again.';
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      setSendError(errorMessage);
      
      // Clear error after 5 seconds
      setTimeout(() => setSendError(null), 5000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const handleTextareaChange = (e) => {
    setMessageText(e.target.value);
    
    // Clear send error when user starts typing a new message
    if (sendError) {
      setSendError(null);
    }
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 100) + 'px';
  };

  const getClientDisplayName = (conversation) => {
    if (conversation.client && conversation.client.firstName && conversation.client.lastName) {
      return `${conversation.client.firstName} ${conversation.client.lastName}`;
    }
    
    const phoneNumber = conversation.phoneNumbers?.[0];
    if (phoneNumber) {
      return phoneNumber;
    }
    
    return 'Unknown Contact';
  };

  const getClientPhoneNumber = (conversation) => {
    if (!conversation.phoneNumbers || conversation.phoneNumbers.length === 0) {
      return 'No phone number';
    }
    
    // If client info is available, use mobile priority logic
    if (conversation.client && conversation.client.phoneNumbers) {
      // First priority: Mobile with SMS consent
      const mobileWithConsent = conversation.client.phoneNumbers.find(
        phone => phone.type === 'Mobile' && phone.smsConsent
      );
      
      if (mobileWithConsent) {
        return mobileWithConsent.number;
      }
      
      // Second priority: Any mobile number
      const anyMobile = conversation.client.phoneNumbers.find(
        phone => phone.type === 'Mobile'
      );
      
      if (anyMobile) {
        return anyMobile.number;
      }
    }
    
    // Fallback to conversation phone numbers - prefer mobile-looking numbers
    const phoneNumbers = conversation.phoneNumbers;
    
    // Try to find a mobile number in the conversation phone numbers
    // Look for numbers that might be mobile based on common patterns
    const possibleMobile = phoneNumbers.find(phone => {
      const number = phone.toString();
      // Simple heuristic: avoid common business numbers like 905-875-3345 (ends with common business patterns)
      return !number.includes('3345') && !number.includes('0000') && !number.includes('1111');
    });
    
    if (possibleMobile) {
      return possibleMobile;
    }
    
    // Final fallback to first number
    return phoneNumbers[0];
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getMessageStatus = (message) => {
    if (message.direction === 'incoming') {
      return message.isRead ? 'Read' : 'Delivered';
    }
    
    switch (message.status) {
      case 'queued':
        return 'Sending...';
      case 'sent':
        return 'Sent';
      case 'delivered':
        return 'Delivered';
      case 'failed':
        return 'Failed';
      default:
        return message.status;
    }
  };

  if (!activeConversation) {
    return (
      <div className="empty-state">
        <UserIcon />
        <h3>No conversation selected</h3>
        <p>Select a conversation to start chatting.</p>
      </div>
    );
  }

  return (
    <div className="chat-thread">
      {/* Header */}
      <div className="chat-header">
        <button
          onClick={() => dispatch(closeActiveConversation())}
          className="chat-back-button"
          title="Back to conversations"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        
        <div className="chat-client-info">
          <h4 className="chat-client-name">
            {getClientDisplayName(activeConversation)}
          </h4>
          <p className="chat-client-phone">
            <PhoneIcon className="h-3 w-3 inline mr-1" />
            {getClientPhoneNumber(activeConversation)}
          </p>
        </div>
      </div>

      {/* General Error Display */}
      {error && (
        <div 
          className="chat-general-error" 
          style={{ 
            padding: '8px 16px', 
            backgroundColor: '#fef2f2', 
            borderTop: '1px solid #fecaca',
            borderBottom: '1px solid #fecaca'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="text-red-600 text-sm">⚠️ {error}</span>
            <button 
              onClick={() => dispatch(clearError())}
              className="text-red-400 hover:text-red-600"
              title="Dismiss"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="chat-messages">
        {isLoading && messages.length === 0 ? (
          <div className="loading-spinner">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
            <span className="ml-2">Loading messages...</span>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <UserIcon />
            <h3>No messages yet</h3>
            <p>Start the conversation by sending a message.</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message._id}
                className={`message ${message.direction}`}
              >
                <div className="message-bubble">
                  {message.body}
                </div>
                <div className="message-time">
                  {formatMessageTime(message.createdAt)}
                </div>
                {message.direction === 'outgoing' && (
                  <div className="message-status">
                    {getMessageStatus(message)}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Error Message */}
      {sendError && (
        <div 
          className="chat-error-container" 
          style={{ 
            padding: '8px 16px', 
            backgroundColor: '#fef2f2', 
            borderLeft: '4px solid #ef4444', 
            margin: '8px 0',
            cursor: 'pointer',
            borderRadius: '4px'
          }}
          onClick={() => setSendError(null)}
          title="Click to dismiss"
        >
          <div className="chat-error-message" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span className="text-red-600 text-sm font-medium">⚠️ {sendError}</span>
            <XMarkIcon className="h-4 w-4 text-red-400 ml-2" />
          </div>
        </div>
      )}

      {/* Message Input */}
      <div className="chat-input-container">
        <form onSubmit={handleSendMessage} className="chat-input-form">
          <textarea
            ref={textareaRef}
            value={messageText}
            onChange={handleTextareaChange}
            onKeyPress={handleKeyPress}
            placeholder="Type a message..."
            className="chat-input"
            rows="1"
            disabled={isSending}
          />
          <button
            type="submit"
            disabled={!messageText.trim() || isSending}
            className="chat-send-button"
            title="Send message"
          >
            {isSending ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <PaperAirplaneIcon className="h-4 w-4" />
            )}
          </button>
        </form>
        
        {/* Character count or typing indicator could go here */}
        <div className="text-xs text-gray-400 mt-1 text-center">
          {process.env.NODE_ENV === 'development' ? (
            <span className="text-amber-600 font-medium">
              DEV MODE: Safe testing numbers or +1-416-669-6505
            </span>
          ) : (
            'Messages are sent via SMS'
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatThread;